import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendMessage, sendMessageToServer, clearLastFailed, resetChat } from "@store/slices/resortSlice";
import { Icon } from "@iconify/react";
import shellyBotIcon from "../../../assets/resort-shelly/images/rs_chat-icon.svg";
import shellyBotModalCloseIcon from "../../../assets/resort-shelly/images/rs_model-close-icon.svg";
import DOMPurify from 'dompurify';
import "../../../assets/resort-shelly/css/rs-styles.css";
import SkeletonInputCtrl from "../../../common/skeletons/SkeletonInputCtrl";

const ResortShellyChatBot = () => {
  const dispatch = useDispatch();
  const messages = useSelector((state) => state.resortShellySlice.messages);
  const loading = useSelector((state) => state.resortShellySlice.loading);
  const lastFailed = useSelector((state) => state.resortShellySlice.lastFailed);
  const rSessionId = useSelector((state) => state.resortShellySlice.sessionId);
  const [heading,setHeading] = useState("Have a question?");
  const [description,setDescription] = useState("Shelly is here to answer and help you learn more about the resort.");
  const chatEndRef = useRef(null);
  const [input, setInput] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [containerStyle, setContainerStyle] = useState({});

  useEffect(() => {
    const container = document.getElementById("shelly-root");
    if (container) {
      setMeta({
        brand: container.getAttribute("data-brand"),
        interface: container.getAttribute("data-interface"),
        hotelCode: container.getAttribute("data-hotel-code"),
        destination: container.getAttribute("data-destination"),
      });
  
      const heading = container.getAttribute("data-heading");
      if (heading && heading.trim() !== "") {
        setHeading(heading);
      }
      const description = container.getAttribute("data-description");
      if (description && description.trim() !== "") {
        setDescription(description);
      }
  
      const width = container.getAttribute("data-width");
      const height = container.getAttribute("data-height");
  
      setContainerStyle({
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
      });
    }
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = () => {
    if (!input.trim() || !meta) return;
    dispatch(sendMessage(input));
    dispatch(sendMessageToServer({ message: input, meta }));
    setInput("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
    handleSend();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setInput(""); // clear input field
    dispatch(resetChat()); // clear chat messages
  };

  const handleRetry = () => {
    if (lastFailed) {
      dispatch(sendMessage(lastFailed.message));
      dispatch(sendMessageToServer(lastFailed));
      dispatch(clearLastFailed());
    }
  };

  return (
    <div className="rs_container">
      {!isModalOpen && (
        <div className="rs_have_a_question-section" style={containerStyle}>
          <div className="rs_header">
            <div className="rs_icon">
              <img src={shellyBotIcon} alt="chatIcon" />
            </div>
            <div className="rs_text">
              <h3><span>New</span> {heading}</h3>
              <p>{description}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="rs_chatbot-input-form">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="rs_chatbot-input"
                placeholder="Type your question..."
              />
              <button type="submit" className="rs_chatbot-chat-btn-send">
                <Icon icon="wpf:sent" />
              </button>
            </div>
          </form>
        </div>
      )}

      {isModalOpen && (
        <div className="rs_modal-overlay">
          <div className="rs_modal">
            <div className="rs_modal-header">
              <div className="rs_chat-header-icon">
                <div className="rs_model-icon">
                  <img src={shellyBotIcon} alt="chatIcon" />
                </div>
                <h3>{heading}</h3>
              </div>
              <button className="rs_close-btn" onClick={closeModal}>
                <img src={shellyBotModalCloseIcon} alt="closeIcon" />
              </button>
            </div>

            <div className="rs_modal-body" id="rs_chat-container">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`rs_chat-message ${msg.sender === "bot"
                    ? "rs_system-message "
                    : "rs_user-message"
                    }`}
                >
                {msg.sender === "bot" ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.text) }}/>
                ) : (
                  msg.text
                )}
                  {msg.type === "error" && (
                    <button
                      onClick={handleRetry}
                      className="rs_retry-button"
                      style={{
                        marginTop: "4px",
                        padding: "2px 8px",
                        fontSize: "12px",
                        cursor: "pointer",
                        backgroundColor: "#ffefc2",
                        border: "1px solid #e0c080",
                        borderRadius: "4px",
                      }}
                    >
                      Retry
                    </button>
                  )}
                </div>
              ))}


              {loading && (
                <div className="rs_chat-message_loading">
                  <div className='rs_bouncing-loader'>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="rs_modal-footer">
              <div className="rs_modal-footer-container" > 
              <div className="rs_chatbot-input-form">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  className="rs_chatbot-input"
                  disabled={loading}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                />
                <button
                  className="rs_chatbot-chat-btn-send"
                  onClick={handleSend}
                  disabled={loading}
                >
                  <Icon icon="wpf:sent" />
                </button>
              </div>

              { !loading && rSessionId == null && <div className='' >
                <div className="rs_activities-btn">
                  <div className="rs_col-4">
                    <button name="userSuggestion" className="rs_cta-btn">
                      How far is it from the airport?
                    </button>
                  </div>
                  <div className="rs_col-4">
                    <button name="userSuggestion" className="rs_cta-btn">
                      What is included in All-Inclusive?
                    </button>
                  </div>

                  <div className="rs_col-4">
                    <button name="userSuggestion" className="rs_cta-btn">
                      What activities are on-site?
                    </button>
                  </div>

                </div>
              </div>
              }
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResortShellyChatBot;
